import { localStorage } from 'lib/core/services/localStorage'
import { Category, CategoryList } from 'lib/types'
import { CATEGORY } from 'lib/core/resources/constants'
import { useCategoryByPath, useCategoryList, useProductByPath } from 'lib/modules/catalog'

interface UseBreadcrumbs {
  breadcrumb: BreadcrumbItem[]
}

interface Props {
  pageTitle?: string
  homePageName?: string
  disableHome?: boolean
  hasSkuBreadcrumb?: boolean
}

export interface BreadcrumbItem {
  name: string
  route?: string
}

export const useBreadcrumbs = ({
  pageTitle,
  homePageName,
  disableHome,
  hasSkuBreadcrumb
}: Props): UseBreadcrumbs => {
  const homeName: BreadcrumbItem = {
    name: homePageName as string,
    route: '/'
  }
  const breadcrumb: BreadcrumbItem[] = !disableHome ? [homeName] : []
  const categoryList = useCategoryList()
  const { category } = useCategoryByPath()
  const { product } = useProductByPath()

  if (product) {
    let productBreadcrumbs: BreadcrumbItem[] = []
    const latestCategory: Category | undefined = localStorage.get('latestCategory')
    const productCategories = product.categories ?? []
    const categoryExists = latestCategory?.id && (
      productCategories.some(cat => cat.id === latestCategory.id) ||
      productCategories.some(
        cat => cat?.breadcrumbs?.some(item => item.category_url_key === latestCategory.attr[CATEGORY.URL_KEY])
      )
    )

    if (latestCategory && categoryExists) {
      productBreadcrumbs = getProductBreadcrumbsFromWindowPath(latestCategory, productBreadcrumbs, categoryList)
    }
    productBreadcrumbs.push({
      name: hasSkuBreadcrumb ? product.sku : product.name
    })
    breadcrumb.push(...productBreadcrumbs)
  } else if (category) {
    const categoryBreadcrumbs: BreadcrumbItem[] = getCategoryBreadcrumbsFromWindowPath(categoryList, category)
    breadcrumb.push(...categoryBreadcrumbs)
  } else {
    breadcrumb.push({
      name: pageTitle as string
    })
  }

  return { breadcrumb }
}

const getProductBreadcrumbsFromWindowPath = (latestCategory: Category | undefined, productBreadcrumbs: BreadcrumbItem[], categories: CategoryList) => {
  const windowPathnameParts = latestCategory?.attr[CATEGORY.URL_PATH].split('/')
  windowPathnameParts?.forEach((slug, i) => {
    const urlPath = windowPathnameParts.slice(0, (i + 1)).join('/')
    const cat = categories.urlPath[urlPath]
    if (cat) {
      productBreadcrumbs.push({
        name: cat.name,
        route: `/${cat.urlPath}`
      })
    }
  })
  return productBreadcrumbs
}

const getCategoryBreadcrumbsFromWindowPath = (categories: CategoryList, category: Category) => {
  const categoryBreadcrumbs: BreadcrumbItem[] = []
  const windowPathnameParts = window.location.pathname.split('/')
  windowPathnameParts.forEach((slug, i) => {
    const urlPath = windowPathnameParts.slice(0, (i + 1)).join('/').slice(1)
    const cat = categories.urlPath[urlPath]
    if (cat) {
      categoryBreadcrumbs.push({
        name: cat.name,
        route: category.id === cat.id ? undefined : `/${cat.urlPath}`
      })
    }
  })
  return categoryBreadcrumbs
}
